import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { useGetDealerApprovalRowHistoryQuery } from '../../../../../../api/endpoints/dealerApproval';
import { camelizeKeys, resolveHistorySourceText, resolveHistoryRowStatusText, resolveOperationText, resolveApprovalStatusText, approvalStatusSeverity, RowStatus } from '../../../../../../utils/dealerApprovalEnums';
import { formatDateOnly } from '../../../../../../utils/dateUtils';

/**
 * Sekme satırı geçmişi modalı.
 * GET /v1/DealerApproval/GetRowHistory?targetType=&rowId=
 */
const RowHistoryDialog = ({ visible, onHide, targetType, rowId, columns = [] }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const shouldFetch = visible && targetType != null && rowId != null;

    const { data, isLoading, isFetching, isError } = useGetDealerApprovalRowHistoryQuery({ targetType, rowId, languageId }, { skip: !shouldFetch, refetchOnMountOrArgChange: true });

    const items = useMemo(() => {
        const list = data?.items || [];
        return list.map((item, index) => ({
            ...item,
            fields: camelizeKeys(item.fields) || {},
            _key: `${item.rowId ?? index}_${index}`
        }));
    }, [data]);

    const fieldColumns = useMemo(() => (columns || []).filter((c) => c?.field), [columns]);

    const statusBody = (row) => {
        // Açık onay talebi varsa talep durumu rozeti (Onayda / İade vb.).
        if (row.approvalRequestStatus != null) {
            return <Tag value={resolveApprovalStatusText(th, row.approvalRequestStatus)} severity={approvalStatusSeverity(row.approvalRequestStatus)} />;
        }
        // Pending shadow ve henüz status yoksa "Onay Bekliyor".
        if (row.source === 3 || row.approvalRequestId != null) {
            return <Tag value={th('dealerApproval.history.rowStatus.pending')} severity="warning" />;
        }
        const text = resolveHistoryRowStatusText(th, row.rowStatus);
        const severity = row.rowStatus === RowStatus.ACTIVE ? 'success' : 'secondary';
        return <Tag value={text} severity={severity} />;
    };

    const footer = (
        <div className="flex justify-content-end">
            <Button label={th('common.close')} icon="pi pi-times" outlined onClick={onHide} />
        </div>
    );

    return (
        <Dialog header={th('dealerApproval.history.title')} visible={visible} style={{ width: '90%', maxWidth: '1100px' }} modal className="p-fluid" onHide={onHide} footer={footer} maximizable>
            {(isLoading || isFetching) && (
                <div className="flex justify-content-center p-4">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                </div>
            )}
            {isError && <div className="text-red-600 p-2">{th('common.error')}</div>}
            {!isLoading && !isFetching && !isError && (
                <DataTable value={items} dataKey="_key" emptyMessage={th('dealerApproval.history.emptyMessage')} showGridlines stripedRows size="small" tableStyle={{ minWidth: '40rem' }}>
                    <Column header={th('dealerApproval.history.column.source')} body={(row) => resolveHistorySourceText(th, row.source)} style={{ minWidth: '110px' }} />
                    <Column header={th('dealerApproval.history.column.rowStatus')} body={statusBody} style={{ minWidth: '120px' }} />
                    <Column header={th('dealerApproval.history.column.operation')} body={(row) => (row.operation != null ? resolveOperationText(th, row.operation) : '-')} style={{ minWidth: '100px' }} />
                    {fieldColumns.map((col) => (
                        <Column
                            key={col.field}
                            header={col.header}
                            body={(row) => {
                                const val = row.fields?.[col.field];
                                if (val == null || val === '') return '-';
                                if (col.bodyField || /date/i.test(col.field)) {
                                    try {
                                        return formatDateOnly(val) || String(val);
                                    } catch {
                                        return String(val);
                                    }
                                }
                                return String(val);
                            }}
                            style={{ minWidth: '100px' }}
                        />
                    ))}
                    <Column field="createdName" header={th('dealerApproval.history.column.createdName')} style={{ minWidth: '110px' }} />
                    <Column header={th('dealerApproval.history.column.createdDate')} body={(row) => (row.createdDate ? formatDateOnly(row.createdDate) : '-')} style={{ minWidth: '120px' }} />
                    <Column field="modifiedName" header={th('dealerApproval.history.column.modifiedName')} style={{ minWidth: '110px' }} />
                    <Column header={th('dealerApproval.history.column.modifiedDate')} body={(row) => (row.modifiedDate ? formatDateOnly(row.modifiedDate) : '-')} style={{ minWidth: '120px' }} />
                </DataTable>
            )}
        </Dialog>
    );
};

export default RowHistoryDialog;
